import { Button, Chip, Menu, MenuItem } from "@mui/material";
import BadgePayment from "elements/BadgePayment";
import BadgeFulfillment, { BADGE_FULFILLMENT_STATUS_MAP } from "elements/BadgeFulfillment";
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ApiRequest } from "GlobalFunctions";
import { PiCaretLeft } from "react-icons/pi";
import CancelOrderDialog from "./components/CancelOrderDialog";
import ConfirmStatusChangeDialog from "./components/ConfirmStatusChangeDialog";
import EventLogs from "elements/EventLogs";
import LoadingButton from '@mui/lab/LoadingButton';


export default function OrdersDetails(props: any) {
  
  const formatPrice = (price: number | string) => {
    return Number(price).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatTaxRate = (taxRate: string) => {
    // Remove the % sign if it exists
    const numericRate = parseFloat(taxRate.replace('%', ''));
    
    // Check if it's an integer
    if (Number.isInteger(numericRate)) {
      return `${numericRate}%`;
    }
    
    // If it has decimals, show up to 3 decimal places (removing trailing zeros)
    return `${numericRate.toFixed(3).replace(/\.?0+$/, '')}%`;
  };
  
    const { setLoading } = props;
    const { order_id } = useParams();
    const navigate = useNavigate();
    const [orderDetails, setOrderDetails] = useState<any>(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [statusMenuWidth, setStatusMenuWidth] = useState<number | undefined>(undefined);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<string>('');
    const [statusChangeLoading, setStatusChangeLoading] = useState(false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleStatusChange = async (status: string) => {
      handleClose();
      setPendingStatus(status);
      setConfirmDialogOpen(true);
    };

    const confirmStatusChange = async (sendNotification?: boolean, shippingCarrierCode?: string, trackingNumber?: string, customCarrier?: string) => {
      setStatusChangeLoading(true);
      
      const body: any = { 
        order_id,
        fulfillment_status_code: pendingStatus,
        send_notification: sendNotification || false
      };

      // Add shipping carrier and tracking number if provided
      if (shippingCarrierCode) {
        body.shipping_carrier_code = shippingCarrierCode;
      }
      if (trackingNumber) {
        body.tracking_number = trackingNumber;
      }
      if (customCarrier) {
        body.custom_carrier = customCarrier;
      }
      
      await ApiRequest({
        method: 'POST',
        url: '/companies/orders/updateFulfillmentStatus',
        body: body,
        setResponse: (response: any) => {
          if (response.status === 'success') {
            setConfirmDialogOpen(false);
            setStatusChangeLoading(false);
            // Reload order details to get updated status
            loadOrderDetails();
          }
        },
        onError: () => {
          setStatusChangeLoading(false);
        }
      });
    };
    
    useEffect(() => {
      if (order_id) {
        loadOrderDetails();
      }
    }, [order_id]);

    useEffect(() => {
      if (!orderDetails?.data) {
        return;
      }

      if (typeof window === 'undefined') {
        return;
      }

      const { order: currentOrder, products: currentProducts } = orderDetails.data;
      if (!currentOrder || !currentProducts) {
        return;
      }

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        return;
      }

      context.font = '400 14px "Roboto","Helvetica","Arial",sans-serif';

      let maxTextWidth = 0;
      Object.values(BADGE_FULFILLMENT_STATUS_MAP).forEach(({ text }) => {
        const measured = context.measureText(text).width;
        if (measured > maxTextWidth) {
          maxTextWidth = measured;
        }
      });

      const additionalText = `(${currentProducts.length})`;
      const currentStatus = BADGE_FULFILLMENT_STATUS_MAP[currentOrder.fulfillment_status_code] || BADGE_FULFILLMENT_STATUS_MAP.ORDER_RECEIVED;
      const currentTextWidth = context.measureText(`${currentStatus.text} ${additionalText}`.trim()).width;
      if (currentTextWidth > maxTextWidth) {
        maxTextWidth = currentTextWidth;
      }

      const iconWidth = 10;
      const textGap = 4;
      const padding = 24;
      const arrowGap = 30;
      const arrowWidth = 16;

      const calculatedWidth = Math.ceil(maxTextWidth + iconWidth + textGap + padding + arrowGap + arrowWidth);

      if (!statusMenuWidth || Math.abs(statusMenuWidth - calculatedWidth) > 1) {
        setStatusMenuWidth(calculatedWidth);
      }
    }, [orderDetails, statusMenuWidth]);

    const loadOrderDetails = async () => {
      setLoading(true);
      await ApiRequest({
        method: 'GET',
        url: '/companies/orders/details',
        query: { order_id },
        setResponse: (response: any) => {
          setOrderDetails(response);
          console.log("Order Details:", response);
        }
      });
      setLoading(false);
    };

  const downloadInvoice = async () => {
    setLoadingInvoice(true);
    ApiRequest({
      method: 'GET',
      url: '/companies/orders/getInvoice',
      query: { order_id },
      setResponse: (response: any) => {
        window.open(response.fileUrl, '_blank');
        setLoadingInvoice(false);
      },
      onError: () => {
        setLoadingInvoice(false);
      }
    });
  };

  const buttonStyle = {
    padding: "4px 10px",
    borderRadius: "5px !important",
    fontSize: "14px",
    fontWeight: "400",
    
  };

  if (!orderDetails) {
    return null;
  }

  const { order, customer, billing, shipping, products, store, custom_invoice, same_as_shipping, shipping_info } = orderDetails.data;

  // Format the status text
  const formatShippingStatus = (status: string) => {
    if (!status) return '';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Format date for shipping info
  const formatShippingDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to copy tracking number to clipboard
  const copyTrackingNumber = (trackingNumber: string) => {
    navigator.clipboard.writeText(trackingNumber);
  };

  return (
    <div id="orders-details" className="order-details">
      <div className="order-details-header">
        <div className="order-details-header-left">
          <span className="order-details-number" onClick={() => navigate('/orders')}>
            <PiCaretLeft size={16} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
            #{order.order_id}
          </span>

          <BadgePayment id={order.payment_status_id} />
          <BadgeFulfillment code={order.fulfillment_status_code} />
        </div>
        <div className="order-details-header-actions">
          {!['READY_FOR_PICK_UP', 'SHIPPED', 'DELIVERED'].includes(order.fulfillment_status_code) && (
            <Button 
              variant="outlined" 
              color="primary" 
              size="small" 
              sx={buttonStyle}
              onClick={() => handleStatusChange('SHIPPED')}
            >
              Mark As Fulfilled
            </Button>
          )}
          <Button 
            variant="outlined" 
            color="primary" 
            size="small" 
            sx={buttonStyle}
            onClick={() => navigate(`/orders/${order_id}/edit`)}
          >
            Edit Order
          </Button>
          {order.payment_status_id != 4 && 
          <Button 
            variant="contained" 
            color="primary" 
            size="small" 
            sx={buttonStyle}
            onClick={() => setCancelDialogOpen(true)}
          >
            Cancel Order
          </Button>
          }
          <LoadingButton 
            variant="contained" 
            color="primary" 
            size="small" 
            loading={loadingInvoice}
            onClick={downloadInvoice}
            sx={{
              fontSize: "14px",
              lineHeight: "150% !important",
              padding: "4px 10px",
              borderRadius: "5px !important",
              backgroundColor: "#ECEBFF",
              color: "#7963FF",
              "&:hover": {
                backgroundColor: "#ECEBFF",
              },
            }}
          >
            Print
          </LoadingButton>
        </div>
      </div>

      <div className="order-details-meta">
        <span className="order-details-timestamp">{order.created_at}</span>
        {!['DELIVERED', 'CANCELED'].includes(order.fulfillment_status_code) ? (
          <div
            onClick={handleClick}
            style={{
              cursor: 'pointer',
              display: 'inline-block',
              width: statusMenuWidth ? `${statusMenuWidth}px` : 'auto'
            }}
          >
            <BadgeFulfillment code={order.fulfillment_status_code} additional_text={`(${products.length})`} showDropdownIcon={true} />
          </div>
        ) : (
          <div
            style={{
              display: 'inline-block',
              width: statusMenuWidth ? `${statusMenuWidth}px` : 'auto'
            }}
          >
            <BadgeFulfillment code={order.fulfillment_status_code} additional_text={`(${products.length})`} showDropdownIcon={false} />
          </div>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          className="order-details-menu"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              backgroundColor: '#FCFCFC',
              padding: '5px 5px 15px !important',
              boxShadow: 'none',
              width: statusMenuWidth ? `${statusMenuWidth}px` : 'auto'
            }
          }}
          MenuListProps={{
            sx: {
              backgroundColor: '#FCFCFC',
              '& .MuiMenuItem-root': {
                backgroundColor: '#FCFCFC !important',
                '&:hover': {
                  backgroundColor: '#FCFCFC !important',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: '#FCFCFC !important',
                },
              },
            }
          }}
        >
          {!['READY_FOR_PICK_UP', 'SHIPPED', 'CANCELED', 'DELIVERED', 'PARTIALLY_REFUNDED', 'RETURNED'].includes(order.fulfillment_status_code) && (
            <>
              <div style={{ 
                padding: '8px 16px 4px 16px', 
                color: '#B9B9BA', 
                fontSize: '9px', 
                fontWeight: '600',
                textTransform: 'capitalize',
                letterSpacing: '0.5px'
              }}>
                Preparing Order
              </div>
              {order.fulfillment_status_code !== 'ORDER_RECEIVED' && (
                <MenuItem onClick={() => handleStatusChange('ORDER_RECEIVED')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
                  <BadgeFulfillment code="ORDER_RECEIVED" />
                </MenuItem>
              )}
              {order.fulfillment_status_code !== 'READY_TO_PACK' && (
                <MenuItem onClick={() => handleStatusChange('READY_TO_PACK')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
                  <BadgeFulfillment code="READY_TO_PACK" />
                </MenuItem>
              )}
              {order.fulfillment_status_code !== 'PACKAGED' && (
                <MenuItem onClick={() => handleStatusChange('PACKAGED')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
                  <BadgeFulfillment code="PACKAGED" />
                </MenuItem>
              )}
              {order.fulfillment_status_code !== 'LABEL_CREATED' && (
                <MenuItem onClick={() => handleStatusChange('LABEL_CREATED')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
                  <BadgeFulfillment code="LABEL_CREATED" />
                </MenuItem>
              )}
            </>
          )}
          
          <div style={{ 
            padding: '12px 16px 4px 16px', 
            color: '#B9B9BA', 
            fontSize: '9px', 
            fontWeight: '600',
            textTransform: 'capitalize',
            letterSpacing: '0.5px',
            marginTop: !['READY_FOR_PICK_UP', 'SHIPPED', 'CANCELED', 'DELIVERED', 'PARTIALLY_REFUNDED', 'RETURNED'].includes(order.fulfillment_status_code) ? '8px' : '0px'
          }}>
            Order Shipped or Closed
          </div>
          {order.fulfillment_status_code !== 'READY_FOR_PICK_UP' && (
            <MenuItem onClick={() => handleStatusChange('READY_FOR_PICK_UP')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
              <BadgeFulfillment code="READY_FOR_PICK_UP" />
            </MenuItem>
          )}
          {order.fulfillment_status_code !== 'SHIPPED' && (
            <MenuItem onClick={() => handleStatusChange('SHIPPED')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
              <BadgeFulfillment code="SHIPPED" />
            </MenuItem>
          )}
          {order.fulfillment_status_code !== 'CANCELED' && (
            <MenuItem onClick={() => handleStatusChange('CANCELED')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
              <BadgeFulfillment code="CANCELED" />
            </MenuItem>
          )}
          {order.fulfillment_status_code !== 'DELIVERED' && (
            <MenuItem onClick={() => handleStatusChange('DELIVERED')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
              <BadgeFulfillment code="DELIVERED" />
            </MenuItem>
          )}
          {order.fulfillment_status_code !== 'PARTIALLY_REFUNDED' && (
            <MenuItem onClick={() => handleStatusChange('PARTIALLY_REFUNDED')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
              <BadgeFulfillment code="PARTIALLY_REFUNDED" />
            </MenuItem>
          )}
          {order.fulfillment_status_code !== 'RETURNED' && (
            <MenuItem onClick={() => handleStatusChange('RETURNED')} style={{paddingBottom:'2px', paddingTop:'2px', paddingLeft:'10px' }}>
              <BadgeFulfillment code="RETURNED" />
            </MenuItem>
          )}
        </Menu>
      </div>

      <div className="order-details-grid">
        <section id="order-details-products-area" className="order-details-card">
          <div className="order-details-section">

            <div className="order-details-section-header">
              <span className="order-details-section-title">Delivery Method</span>
              <span className="order-details-section-subtitle">{order.shipping_method}</span>

              {shipping_info && shipping_info.tracking_number && (
                <div className="shipping-tracking-container">
                  {orderDetails?.data?.is_shippo && (
                    <div className="shipping-tracking-header">
                      <span className="shipping-tracking-title">{formatShippingStatus(shipping_info.status || shipping_info.tracking_status)}</span>
                      <span className="shipping-tracking-date">{formatShippingDate(shipping_info.object_updated)}</span>
                    </div>
                  )}

                  <div className="shipping-tracking-body">
                    <div className="shipping-tracking-number">
                      {(orderDetails?.data?.is_shippo ? shipping_info.provider_image_75 : shipping_info.logo_url) && (
                        <img 
                          src={orderDetails?.data?.is_shippo ? shipping_info.provider_image_75 : shipping_info.logo_url} 
                          alt="Carrier Logo" 
                          className="shipping-carrier-logo"
                        />
                      )}
                      <span className="tracking-label">Tracking Number:</span>
                      <span className="tracking-number">{shipping_info.tracking_number}</span>
                      <a 
                        onClick={() => copyTrackingNumber(shipping_info.tracking_number)}
                        className="copy-tracking-button"
                      >
                        Copy
                      </a>
                    </div>

                    {shipping_info.tracking_url_provider && (
                      <div className="shipping-tracking-actions">
                        <a 
                          href={shipping_info.tracking_url_provider} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="tracking-link"
                        >
                          View Details In {orderDetails?.data?.is_shippo ? (shipping_info.rate_details?.provider?.toUpperCase() || 'Carrier') : (shipping_info.provider || 'Carrier')}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            <div className="order-details-products">
              {products.map((product: any, index: number) => (
                <article key={index} className="order-details-product">
                  <div className="order-details-product-main">
                    <div className="order-details-product-image">
                      {product.product_image && (
                        <img src={product.product_image} alt={product.product_name} />
                      )}
                    </div>
                    <div className="order-details-product-text">
                      <p className="order-details-product-name">{product.product_name}</p>
                      {product.custom_inputs && product.custom_inputs.length > 0 && (
                        <ul className="order-details-product-options">
                          {product.custom_inputs
                            .filter((input: any) => !['name', 'price'].includes(input.name) && input.value && typeof input.value === 'string' && input.value.trim() !== '')
                            .map((input: any, i: number) => (
                              <li key={i} style={{ textTransform: 'capitalize' }}>{input.name}: {input.value}</li>
                            ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="order-details-product-pricing">
                    <span className="order-details-price">${formatPrice(product.product_price)}</span>
                    <span className="order-details-quantity">x</span>
                    <span className="order-details-quantity">{product.quantity}</span>
                    <span className="order-details-price">${formatPrice(product.total_price)}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="order-details-totals">
            <div className="order-details-total-row">
              <span className="order-details-total-spacer" aria-hidden="true" />
              <div className="order-details-total-info">
                <span className="order-details-total-label">Subtotal</span>
              </div>
              <span className="order-details-price">${formatPrice(order.subtotal_price)}</span>
            </div>
            {Number(order.total_discount) > 0 && (
              <div className="order-details-total-row">
                <span className="order-details-total-spacer" aria-hidden="true" />
                <div className="order-details-total-info">
                  <span className="order-details-total-label">Discount</span>
                </div>
                <span className="order-details-price">-${formatPrice(order.total_discount)}</span>
              </div>
            )}
            <div className="order-details-total-row">
              <span className="order-details-total-spacer" aria-hidden="true" />
              <div className="order-details-total-info">
                
                <div className="order-details-total-shipping">
                  <div><span className="order-details-total-label">Shipping</span></div>
                  <div className="order-details-total-shipping-content">
                    <span className="order-details-name">{shipping.name} {shipping.lastname}</span>
                    <span className="order-details-address">{shipping.address}</span>
                    <span className="order-details-address">{shipping.city_name} {shipping.region_name} {shipping.zip_code}</span>
                    <span className="order-details-address">{shipping.country_name}</span>
                  </div>
                  <div></div>
                </div>
              </div>
              <span className="order-details-price">${formatPrice(order.shipping_price)}</span>
            </div>
            {order.discriminated_taxes && order.discriminated_taxes.length > 0 ? (
              <div className="order-details-total-row">
                <span className="order-details-total-spacer" aria-hidden="true" />
                <div className="order-details-total-info">
                  <div className="order-details-total-shipping">
                    <div><span className="order-details-total-label">Taxes</span></div>
                    <div className="order-details-total-shipping-content">
                      {order.discriminated_taxes.map((tax: any, index: number) => (
                        <span key={index} className="order-details-name" style={{ marginBottom: index < order.discriminated_taxes.length - 1 ? '10px' : '0' }}>{tax.tax_type} ({formatTaxRate(tax.tax_rate)})</span>
                      ))}
                    </div>
                    <div></div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                  {order.discriminated_taxes.map((tax: any, index: number) => (
                    <span key={index} className="order-details-price">${formatPrice(tax.tax_amount)}</span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="order-details-total-row">
                <span className="order-details-total-spacer" aria-hidden="true" />
                <div className="order-details-total-info">
                  <span className="order-details-total-label">Taxes</span>
                </div>
                <span className="order-details-price">${Number(order.total_tax).toFixed(2)}</span>
              </div>
            )}
          </div>
          
          <div className="order-details-total-row order-details-total-row-highlight">
            <span className="order-details-total-spacer" aria-hidden="true" />
            <div className="order-details-total-info">
              <span className="order-details-total-label">Total</span>
            </div>
            <span className="order-details-price">${formatPrice(order.total_price)}</span>
          </div>
        </section>

        <aside id='order-details-summary' className="order-details-card">

          <div className="order-details-summary-section">
            <span className="order-details-summary-title">Customer</span>
            <span className="order-details-highlight">{customer.name} {customer.lastname}</span>
            <p className="order-details-customer-meta">{customer.customer_order_count} {customer.customer_order_count > 1 ? 'Orders' : 'Order'}</p>
          </div>

          <div className="order-details-summary-section">
            <span className="order-details-summary-title">Contact Information</span>
            <span className="order-details-highlight">{customer.email}</span>
            <span className="order-details-highlight">{customer.phone}</span>
          </div>


          <div className="order-details-summary-section">
            <span className="order-details-summary-title">Shipping Address</span>
            <span className="dimmed">{shipping.name} {shipping.lastname}</span>
            <span className="dimmed">{shipping.address}{shipping.address2 && shipping.address2 !== '.' ? `, ${shipping.address2}` : ''}</span>
            <span className="dimmed">{shipping.city_name} {shipping.region_name} {shipping.zip_code}</span>
            <span className="dimmed">{shipping.country_name}</span>
          </div>

          <div className="order-details-summary-section">
            <span className="order-details-summary-title">Billing Address</span>
            {same_as_shipping ? (
              <span className="dimmed">Same As Shipping Address</span>
            ) : (
              <>
                <span className="dimmed">{billing.name} {billing.lastname}</span>
                <span className="dimmed">{billing.address}{billing.address2 && billing.address2 !== '.' ? `, ${billing.address2}` : ''}</span>
                <span className="dimmed">{billing.city} {billing.state_name} {billing.zip_code}</span>
                <span className="dimmed">{billing.country_name}</span>
              </>
            )}
          </div>
        </aside>
      </div>

      {/* Order Events List */}
      <div className="orders-events-section">
        <h2>History</h2>
        <EventLogs 
          url="/companies/orders/eventList"
          queryParams={{ order_id }}
          emptyMessage="No Logged Events Yet — Your First Event Will Appear Here."
        />
      </div>

      <CancelOrderDialog 
        open={cancelDialogOpen}
        setOpen={setCancelDialogOpen}
        loading={cancelLoading}
        total_price={order?.total_price}
        order_id={order_id}
        currency={store?.currency}
        loadOrderDetails={loadOrderDetails}
        card_logo_url={order?.card_logo_url}
      />

      <ConfirmStatusChangeDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        loading={statusChangeLoading}
        oldStatus={order?.fulfillment_status_code || 'ORDER_RECEIVED'}
        newStatus={pendingStatus}
        onConfirm={confirmStatusChange}
        isShippo={orderDetails?.data?.is_shippo || false}
      />
    </div>
  );
}